import Info from "../../components/Info";
import ResourceList from "../../components/ResourceList";
import Text from "../../components/Text";
import LessonPlanLinks from "./LessonPlanLinks";
import EditSyllabusButton from "../../components/EditSyllabusButton";
import Issues from "./Issues";
import ResourcePreviewGrid, { CopyButton, iconMap, ResourceCard } from "./ResourcePreviewGrid";
import {
  BookOpen,
  BookText,
  FileText,
  MessageSquare,
  Sparkles,
  MonitorPlay,
  ClipboardCheck,
  Presentation,
  ExternalLink,
} from "lucide-react";
import useCTX from "../../hooks/useCTX";
import { usePrimaryColor } from "../../components/PrimaryColorContext";

/**
 * LessonContent - Displays the content tab for a lesson including description,
 * vocabulary, grammar, resources, homework, and materials request.
 * Uses a card-based layout with copy-to-clipboard support and auto-embed previews.
 * @param {object} props
 * @param {object} [props.cLesson] - Curriculum lesson data
 * @param {object} props.lesson - Lesson record from DB
 * @param {function} props.setLesson - State setter for lesson
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 3.0.0
 * @since 2026-04-14
 */
export default function LessonContent({ cLesson, lesson, setLesson }) {
  const { findStaff } = useCTX();
  const { primaryColor } = usePrimaryColor();

  if (!cLesson && lesson.unit) {
    return (
      <DrawTest
        unit={lesson.unit}
        lesson={lesson}
      />
    );
  }

  if (!cLesson) {
    return (
      <div className="border border-slate-200/80 bg-white rounded-[12px] p-12 text-center shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="bg-slate-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <BookOpen size={32} className="text-slate-300" />
        </div>
        <p className="text-slate-400 font-medium">No lesson content available for this class</p>
      </div>
    );
  }

  const hasMedia =
    (cLesson.ppts && cLesson.ppts.length > 0) ||
    (cLesson.videos && cLesson.videos.length > 0) ||
    (cLesson.modelLessonPlan && cLesson.modelLessonPlan.length > 0) ||
    (cLesson.resources && cLesson.resources.length > 0);

  const otherPlans = cLesson.otherLessonPlans;

  return (
    <div className="space-y-6">
      {/* Lesson Description */}
      <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <BookText size={20} className={`text-${primaryColor}-800`} />
            Lesson Description
          </h3>
          <EditSyllabusButton
            syllabus={cLesson.syllabus._id}
            unit={cLesson.unit._id}
            lesson={cLesson._id}
          />
        </div>
        <p className="text-slate-500 leading-relaxed">{cLesson.description || "No description available"}</p>
        {cLesson.bookPages && (
          <p className="mt-4 text-sm font-medium inline-flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-[10px] border border-slate-200">
            <BookOpen size={14} className={`text-${primaryColor}-800`} />
            <strong className="text-slate-700">Pages in book:</strong>
            <span className="text-slate-500">{cLesson.bookPages}</span>
          </p>
        )}
      </div>

      {/* Vocabulary & Grammar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Sparkles size={20} className={`text-${primaryColor}-800`} />
              Vocabulary
            </h3>
            {cLesson.vocabulary?.length > 0 && (
              <CopyButton data={cLesson.vocabulary} />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {cLesson.vocabulary?.length > 0
              ? cLesson.vocabulary.map((v, i) => (
                  <span key={i} className="px-3 py-1.5 bg-slate-50 rounded-[10px] text-sm font-medium border border-slate-200 text-slate-700">
                    {v}
                  </span>
                ))
              : <span className="text-slate-400 italic text-sm">No vocabulary listed</span>}
          </div>
        </div>

        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <BookOpen size={20} className={`text-${primaryColor}-800`} />
              Grammar
            </h3>
            {cLesson.grammar?.length > 0 && (
              <CopyButton data={cLesson.grammar} />
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {cLesson.grammar?.length > 0
              ? cLesson.grammar.map((g, i) => (
                  <span key={i} className="px-3 py-1.5 bg-slate-50 rounded-[10px] text-sm font-medium border border-slate-200 text-slate-700">
                    {g}
                  </span>
                ))
              : <span className="text-slate-400 italic text-sm">No grammar points listed</span>}
          </div>
        </div>
      </div>

      {/* Conversation */}
      {cLesson.unit?.conversation && (
        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <MessageSquare size={20} className={`text-${primaryColor}-800`} />
              Conversation
            </h3>
            <CopyButton data={cLesson.unit.conversation} />
          </div>
          <pre className="whitespace-pre-wrap text-sm text-slate-500 bg-slate-50 p-5 rounded-[10px] border border-slate-200 font-sans leading-relaxed">
            {cLesson.unit.conversation}
          </pre>
        </div>
      )}

      {/* Lesson Plan Links */}
      <LessonPlanLinks
        lesson={lesson}
        cLesson={cLesson}
      />

      {/* Other Lesson Plans */}
      {otherPlans && otherPlans.length > 0 && (
        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <BookOpen size={20} className={`text-${primaryColor}-800`} />
            Other Lesson Plans
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {otherPlans.map((r, i) => (
              <ResourceCard
                key={r._id || r.link || i}
                resource={r}
              />
            ))}
          </div>
        </div>
      )}

      {/* Teaching Materials Request */}
      <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
          <FileText size={20} className={`text-${primaryColor}-800`} />
          Teaching Materials Required
        </h3>
        <Text
          value={lesson.materialRequests}
          setValue={(materialRequests) =>
            setLesson((prev) => ({ ...prev, materialRequests }))
          }
          url={`/add-materials-request/${lesson._id}`}
          field="materialRequests"
          fullWidth
          multiline
          rows={4}
          label="Describe what materials you need..."
          fontSize={14}
        />
        <div className="text-xs text-slate-400 mt-2">
          {lesson.materialRequests && lesson.materialRequestsCompleted
            ? `Fulfilled by ${
                findStaff(lesson.materialRequestsCompletedBy)
                  ? findStaff(lesson.materialRequestsCompletedBy).name
                  : "Unknown person"
              } on ${new Date(
                lesson.materialRequestsCompleted
              ).fDate()} at ${new Date(
                lesson.materialRequestsCompleted
              ).fTime()}`
            : lesson.materialRequests
              ? "Pending fulfillment"
              : ""}
        </div>
      </div>

      {/* Media & Resources */}
      {hasMedia && (
        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
            <MonitorPlay size={22} className={`text-${primaryColor}-800`} />
            Media &amp; Resources
          </h3>
          <div className="space-y-6">
            {resList.map((r) => {
              if (!cLesson[r.field] || cLesson[r.field].length === 0) return null;
              return (
                <ResourcePreviewGrid
                  key={r.field}
                  resources={cLesson[r.field]}
                  label={r.label}
                  previewType={r.previewType}
                  icon={iconMap[r.field] || r.icon}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Homework */}
      {(cLesson.homework || (cLesson.homeworkLinks && cLesson.homeworkLinks.length > 0)) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {cLesson.homework && (
            <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <FileText size={20} className={`text-${primaryColor}-800`} />
                Homework
              </h3>
              <p className="text-slate-500 leading-relaxed">{cLesson.homework}</p>
            </div>
          )}
          {cLesson.homeworkLinks && cLesson.homeworkLinks.length > 0 && (
            <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <BookOpen size={20} className={`text-${primaryColor}-800`} />
                Homework Links
              </h3>
              <ResourceList
                value={cLesson}
                field="homeworkLinks"
                type="qr"
              />
            </div>
          )}
        </div>
      )}

      <Issues cLesson={cLesson} />
    </div>
  );
}

const resList = [
  { field: "ppts", label: "Presentations", previewType: "canva", icon: iconMap.canva },
  { field: "videos", label: "Videos", previewType: "youtube", icon: iconMap.youtube },
  { field: "modelLessonPlan", label: "Model lesson plan", previewType: "auto", icon: iconMap.modelLessonPlan },
  { field: "resources", label: "Teaching resources", previewType: "auto", icon: iconMap.resources },
];

/**
 * DrawTest - Displays test info when no curriculum lesson but unit exists
 * @param {object} props
 * @param {object} props.unit - Unit data
 * @param {object} props.lesson - Lesson record
 * @returns {JSX.Element}
 * @author opencode (glm-5.1) + Forge
 * @version 3.0.0
 * @since 2026-04-14
 */
function DrawTest({ unit, lesson }) {
  const { primaryColor } = usePrimaryColor();
  const testName = unit.tests?.[0]?.name;

  return (
    <div className="space-y-6">
      {/* Test Overview */}
      <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <ClipboardCheck size={20} className={`text-${primaryColor}-800`} />
            Test Overview
          </h3>
          <EditSyllabusButton syllabus={lesson.syllabus} unit={unit._id} />
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="font-semibold text-slate-800 text-base">{lesson.syllabusName}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500">
            Unit {unit.unitNumber}: {unit.name}
          </span>
          {testName && (
            <>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500">{testName}</span>
            </>
          )}
        </div>
      </div>

      {/* Conversation */}
      {unit.conversation && (
        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <MessageSquare size={20} className={`text-${primaryColor}-800`} />
              Conversation
            </h3>
            <CopyButton data={unit.conversation} />
          </div>
          <pre className="whitespace-pre-wrap text-sm text-slate-500 bg-slate-50 p-5 rounded-[10px] border border-slate-200 font-sans leading-relaxed">
            {unit.conversation}
          </pre>
        </div>
      )}

      {/* Test Materials */}
      {unit.tests && unit.tests.length > 0 && (
        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <FileText size={20} className={`text-${primaryColor}-800`} />
            Test Materials
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unit.tests.map((t, i) => (
              <a
                key={t._id || i}
                href={t.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 p-3 bg-slate-50 hover:bg-${primaryColor}-50 border border-slate-200 hover:border-${primaryColor}-800/20 rounded-[10px] transition-all duration-150 group cursor-pointer`}
              >
                <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                  <FileText size={18} className={`text-${primaryColor}-800`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 text-sm truncate">{t.name || "Test"}</div>
                  <div className="text-xs text-slate-400">Open test</div>
                </div>
                <ExternalLink size={14} className={`text-slate-400 group-hover:text-${primaryColor}-800`} />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Test PowerPoints */}
      {unit.testppt && unit.testppt.length > 0 && (
        <div className="border border-slate-200/80 bg-white rounded-[12px] p-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
            <Presentation size={20} className={`text-${primaryColor}-800`} />
            Test PowerPoints
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unit.testppt.map((t, i) => (
              <a
                key={t._id || i}
                href={t.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 p-3 bg-slate-50 hover:bg-${primaryColor}-50 border border-slate-200 hover:border-${primaryColor}-800/20 rounded-[10px] transition-all duration-150 group cursor-pointer`}
              >
                <div className="p-2 bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform">
                  <Presentation size={18} className={`text-${primaryColor}-800`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 text-sm truncate">{t.name || "PowerPoint"}</div>
                  <div className="text-xs text-slate-400">Open presentation</div>
                </div>
                <ExternalLink size={14} className={`text-slate-400 group-hover:text-${primaryColor}-800`} />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
